/**
 * The send path's `PaymentRouter`. The fallback is not written here: a rail whose
 * `available()` returns false or throws is dropped from `options()` without
 * taking the router with it, replacing a six-case refusal enum. Amounts are
 * receiver-exact; the screen translates from "what leaves" at the boundary,
 * which keeps "send max" working.
 */
import {
  PaymentRouter,
  arkRail,
  arkTarget,
  btcTarget,
  invoiceTarget,
  makeHandle,
  type Asset,
  type IntentFeeConfig,
  type IWallet,
  type PaymentHandle,
  type PaymentRail,
  type PaymentRequest,
  type RouteQuote,
  type RouteResult,
} from '@arkade-os/sdk'
import {
  LIGHTNING_RAIL,
  ONCHAIN_SWAP_RAIL,
  lightningRail,
  onchainSwapRail,
  type Outcome,
  type SwapRailClient,
} from '@arkade-os/swap'
import { marketCorridor, sideLimits, type DiscoveredMarket } from '@arkade-os/solver-discovery'
import { createLnurlClient, type LnurlClient } from '@arkade-os/lnurl-client'
import {
  firstQuote,
  isLnurlRail,
  LNURL_ARKADE_RAIL,
  LNURL_LIGHTNING_RAIL,
  lnurlRails,
} from '@arkade-os/lnurl-client/arkade'
import { collaborativeExitWithFees, planCollaborativeExit, sendAssets } from './asp'
import { decodeInvoice } from './bolt11'
import { consoleError } from './logs'
import { prettyNumber } from './format'
import type { AppIntentState } from './appIntent'
import type { SendInfo } from '../providers/flow'
import { defaultFee } from './constants'

export const WALLET_EXIT_RAIL = 'onchain'

export const ASSET_RAIL = 'asset'

export const ONCHAIN_ROUTE_LOG = 'onchain send:'

export { LIGHTNING_RAIL, LNURL_ARKADE_RAIL, LNURL_LIGHTNING_RAIL, ONCHAIN_SWAP_RAIL }

/** Not the SDK's `onchainRail`: that offboards with its own coin selection. */
export const walletExitRail = (deps: {
  outputFee: () => number
  intentFee: () => IntentFeeConfig | undefined
  dust: () => number
}): PaymentRail => ({
  id: WALLET_EXIT_RAIL,
  // The same classification the SDK's `onchainRail` uses, so this rail cannot
  // claim an ark address or an invoice if the router is reused for them.
  match: (req) => btcTarget(req.raw) !== undefined,
  available: (req) => (req.amount ?? 0) > 0,
  quote: async (req, ctx) => {
    const amount = req.amount!
    const exitFee = deps.outputFee()
    const plan = await planCollaborativeExit(ctx.wallet, amount + exitFee, deps.intentFee(), deps.dust())
    const fee = exitFee + plan.fee
    const total = amount + fee
    return {
      railId: WALLET_EXIT_RAIL,
      amount,
      fee,
      total,
      send: async () =>
        makeHandle(WALLET_EXIT_RAIL, async (emit) => {
          const txid = await collaborativeExitWithFees(ctx.wallet, plan, amount, req.raw)
          const result = { railId: WALLET_EXIT_RAIL, txid }
          emit({ status: 'settled', result })
          return result
        }),
    }
  },
})

/** Assets ride the deps, not the request: a `PaymentRequest` carries sats. */
export const assetRail = (deps: { assets: Asset[] }): PaymentRail => ({
  id: ASSET_RAIL,
  match: (req) => arkTarget(req.raw) !== undefined,
  available: () => deps.assets.length > 0,
  quote: async (req, ctx) => ({
    railId: ASSET_RAIL,
    amount: 0,
    fee: defaultFee,
    total: defaultFee,
    send: async () =>
      makeHandle(ASSET_RAIL, async (emit) => {
        const txid = await sendAssets(ctx.wallet, arkTarget(req.raw)!, deps.assets)
        // Terminal in one step: no counterparty acts, so the txid IS settlement.
        const result = { railId: ASSET_RAIL, txid }
        emit({ status: 'settled', result })
        return result
      }),
  }),
})

/** Optional per-rail deps: a rail whose deps are absent is not registered,
 *  which is the drop `available()` performs, one step earlier. */
export interface SendRouterDeps {
  wallet: IWallet
  /** The driving tab's client; a tab without the Web Lock has none. */
  client?: SwapRailClient
  /** sat/vB the L1 claim is priced at; it comes out of the recipient's payout. */
  claimFeeRateSatVb?: number
  outputFee?: () => number
  /** The operator's programs, which price each coin the exit spends. */
  intentFee?: () => IntentFeeConfig | undefined
  /** The server's floor on a VTXO output, which the exit's change must clear. */
  dust?: () => number
  assets?: Asset[]
  lnurl?: LnurlClient
}

export const createSendRouter = (deps: SendRouterDeps): PaymentRouter => {
  const router = new PaymentRouter({
    wallet: deps.wallet,
    prefs: {
      priority: [
        ONCHAIN_SWAP_RAIL,
        WALLET_EXIT_RAIL,
        LNURL_ARKADE_RAIL,
        LNURL_LIGHTNING_RAIL,
        LIGHTNING_RAIL,
        ASSET_RAIL,
      ],
    },
  })

  if (deps.client && deps.claimFeeRateSatVb) {
    router.use(onchainSwapRail(deps.client, { claimFeeRateSatVb: deps.claimFeeRateSatVb }))
  }
  if (deps.outputFee) {
    // Refused at wiring rather than dropped: without them an exit underpays or emits a change arkd refuses.
    if (!deps.intentFee || !deps.dust) {
      throw new Error('send router: the exit rail needs intentFee and dust alongside outputFee')
    }
    router.use(walletExitRail({ outputFee: deps.outputFee, intentFee: deps.intentFee, dust: deps.dust }))
  }
  if (deps.client) router.use(lightningRail(deps.client))
  if (deps.assets) router.use(assetRail({ assets: deps.assets }))
  // The Arkade leg needs no swap client, so without one only the Lightning leg drops: the case of
  // an LNURL send from a tab that is not driving swaps (`sendRouter({ swapsOptional })`).
  const lnurl = lnurlRails({
    client: deps.lnurl ?? createLnurlClient(),
    arkade: arkRail(),
    ...(deps.client ? { lightning: lightningRail(deps.client) } : {}),
  })
  for (const rail of lnurl) router.use(rail)
  return router
}

export { isLnurlRail }

export const quoteLnurl = async (router: PaymentRouter, target: string, amount: number): Promise<RouteQuote> => {
  const options = (await router.options({ raw: target, amount })).filter((o) => isLnurlRail(o.railId))
  if (!options.length) throw new Error('No route for this LNURL payment')
  return firstQuote(options, (railId, err) => consoleError(err, `lnurl send: ${railId} could not quote`))
}

/** An amount-bearing invoice pins the take leg by existing, and the client throws
 *  `AmountMismatch` on a request pinning it twice — even when the two agree. */
export const lnSendRequest = (invoice: string, satoshis?: number): PaymentRequest => {
  const target = invoiceTarget(invoice)
  if (target !== undefined) {
    try {
      if (decodeInvoice(target).amountSats > 0) return { raw: invoice }
    } catch {
      // Undecodable is the rail's refusal to name, not this builder's.
    }
  }
  return { raw: invoice, ...(satoshis === undefined ? {} : { amount: satoshis }) }
}

/**
 * A market whose quote leg is Lightning.
 *
 * The rail is the asset id's chain namespace (`bolt11:…`). `quote_corridor:
 * "lightning"` is the legacy field, which {@link marketCorridor} still reads.
 */
export const isLightningMarket = (market: DiscoveredMarket): boolean => marketCorridor(market, 'quote') === 'bolt11'

/** Why the Lightning rail dropped itself. `options()` logs the rail's error and
 *  returns only survivors, so bounds are named only where checked and missed. */
export const lnSendRefusal = (markets: DiscoveredMarket[], satoshis?: number): string => {
  const bounds = markets
    .filter(isLightningMarket)
    .map((m) => sideLimits(m, 'quote'))
    .filter((limits): limits is NonNullable<typeof limits> => limits !== null)
  if (bounds.length === 0) return 'No Lightning solver available'
  const min = bounds.reduce((low, b) => (b.min < low ? b.min : low), bounds[0].min)
  const max = bounds.reduce((high, b) => (b.max > high ? b.max : high), bounds[0].max)
  const span = `${prettyNumber(Number(min))}-${prettyNumber(Number(max))} sats`
  const amount = satoshis === undefined ? undefined : BigInt(satoshis)
  if (amount !== undefined && !bounds.some((b) => amount >= b.min && amount <= b.max)) {
    return `Amount outside solver bounds (${span})`
  }
  return `No Lightning solver took this payment (solvers take ${span}). The router does not report why a rail dropped; the reason is in the console log.`
}

/** The Lightning analogue of {@link quoteIsForThisSend}. By payment hash: the
 *  rail's `meta` carries `quote.lock.hash` and never the BOLT11, so a
 *  `meta.invoice` test would refuse every send rather than a mismatched one. */
export const quoteIsForThisInvoice = (quote: Pick<RouteQuote, 'meta'>, invoice: string): boolean => {
  const target = invoiceTarget(invoice)
  if (target === undefined) return false
  const quoted = quote.meta?.paymentHash
  if (typeof quoted !== 'string' || !quoted) return false
  try {
    const { paymentHash } = decodeInvoice(target)
    return Boolean(paymentHash) && paymentHash.toLowerCase() === quoted.toLowerCase()
  } catch {
    return false
  }
}

/** What an on-chain send really costs, asked before the sign screen commits to a
 *  total. `calcOnchainOutputFee()` is the collaborative exit's fee and no other
 *  rail's, so pricing the screen with it leaves {@link quoteIsForThisSend} a
 *  ceiling only the exit can meet. The spend re-quotes against this figure. */
export const previewOnchainCost = async (
  router: PaymentRouter,
  address: string,
  amount: number,
): Promise<Pick<RouteQuote, 'amount' | 'fee' | 'total'> | undefined> => {
  for (const option of await router.options({ raw: address, amount })) {
    try {
      const quote = await option.quote()
      // A rail paying something else is not the one being priced.
      if (quote.amount === amount) return { amount: quote.amount, fee: quote.fee, total: quote.total }
    } catch (err) {
      // Not left to the spend: it can reach a different answer, and then the
      // screen is committed to this pass's price with nobody having logged why.
      consoleError(err, `${ONCHAIN_ROUTE_LOG} ${option.railId} could not be priced`)
    }
  }
  return undefined
}

/** `options()` drops a rail that refuses, nothing bounds one that never answers,
 *  and this runs BEFORE Tap to Sign appears — the RFQ's deadline is 30s. Expiring
 *  keeps the exit's numbers, which {@link quoteIsForThisSend} holds a solver to. */
export const PRICING_BUDGET_MS = 8_000

/** `work` must not reject; the caller owns that, so nothing is left unhandled. */
export const withinPricingBudget = <T>(work: Promise<T>, budgetMs = PRICING_BUDGET_MS): Promise<T | undefined> => {
  let timer: ReturnType<typeof setTimeout>
  return Promise.race([
    work,
    new Promise<undefined>((resolve) => {
      timer = setTimeout(() => resolve(undefined), budgetMs)
    }),
  ]).finally(() => clearTimeout(timer))
}

/** Resolve when the rail reports the funding done, not when the swap ends.
 *  `"sent"` is core's word for it and both swap rails emit it once the covenant
 *  holds the money; `settled()` waits for the counterparty, an L1 confirmation
 *  away on `arkade -> onchain`. `done` guards the replay `subscribe` fires
 *  before it has returned the unsubscribe. */
export const fundedResult = (handle: PaymentHandle): Promise<RouteResult | undefined> =>
  new Promise((resolve, reject) => {
    let done = false
    let stop: (() => void) | undefined
    const unsubscribe = handle.subscribe((update) => {
      if (done) return
      if (update.status === 'sent' || update.status === 'settled') {
        done = true
        stop?.()
        resolve(update.result)
      } else if (update.status === 'failed') {
        done = true
        stop?.()
        reject(update.error ?? new Error('Payment failed'))
      }
    })
    stop = unsubscribe
    if (done) unsubscribe()
  })

/** Quoting lazily already removes the stale quote behind the wrong-address bug.
 *  This is the belt to that braces — a rail may quote worse than advertised. */
export const quoteIsForThisSend = (
  quote: Pick<RouteQuote, 'amount' | 'total'>,
  screen: { destination?: string; satoshis?: number; total?: number },
  routedAddress: string,
): boolean =>
  screen.destination === routedAddress && quote.amount === screen.satoshis && quote.total <= (screen.total ?? 0)

export const onchainSendNeedsDriver = (sendInfo: SendInfo, outcomeOf: (id: string) => Outcome | undefined): boolean =>
  Boolean(sendInfo.address) &&
  !sendInfo.arkAddress &&
  !sendInfo.invoice &&
  !sendInfo.account &&
  !sendInfo.assets?.length &&
  sendInfo.railId !== WALLET_EXIT_RAIL &&
  Boolean(sendInfo.swapId || sendInfo.txid || sendInfo.railId === ONCHAIN_SWAP_RAIL) &&
  (!sendInfo.swapId || outcomeOf(sendInfo.swapId) !== 'claimed')

export const appSendNeedsDriver = (
  intent: AppIntentState | undefined,
  sendInfo: SendInfo,
  outcomeOf: (id: string) => Outcome | undefined,
): boolean => {
  if (intent?.status !== 'send' || !intent.callback) return false
  const committed = intent.committedOnchain
  return (
    Boolean(committed && (!committed.swapId || outcomeOf(committed.swapId) !== 'claimed')) ||
    onchainSendNeedsDriver(sendInfo, outcomeOf)
  )
}
